import {
  Button,
  DatePicker,
  Form,
  Input,
  Select,
  TimePicker,
  Typography,
} from 'antd';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { DrawerContext } from '~/providers/DrawerProvider';
import { useLoadingSetter } from '~/providers/LoadingProvider';
import message from '~/utils/message';
import { useCheckPermission } from '~/store/permission';
import { api, Order } from '@restaurant/api';
import { tableOptions } from '~/constants/common';
import {
  checkIsRequireTableNumber,
  ORDER_TYPE_OPTIONS,
} from '@restaurant/constants';
import { OrderType } from '@restaurant/types';
import { DeliveryFormFields } from '~/App/Menu/components/OrderInfo/InputDelivery';
import {
  transformFromDeliveryFormValues,
  transformToDeliveryFormValues,
} from '~/App/Menu/components/OrderInfo/InputDelivery/utils';
import { OrderDeliveryFormValues } from '~/App/Menu/components/OrderInfo/InputDelivery/types';

const OrderInfoPanel = ({
  order,
  onClose,
}: {
  order: Order;
  onClose: () => void;
}) => {
  const setLoading = useLoadingSetter();
  const [form] = Form.useForm();
  const checkPermission = useCheckPermission();
  const isUserAllowToUpdate = checkPermission('orderInfo:u');

  const initialValues = useMemo(
    () => ({
      orderType: order.orderType,
      tableNumber: order.tableNumber,
      customerName: order.customerName,
      ...(order.delivery
        ? { delivery: transformToDeliveryFormValues(order.delivery) }
        : {}),
    }),
    [order]
  );

  // Handle order type change.
  const handleOrderTypeChange = (value: OrderType) => {
    if (!checkIsRequireTableNumber(value)) {
      form.setFieldsValue({ tableNumber: undefined });
    }
  };

  // Handle finish.
  const handleFinish = async (data: {
    orderType: string;
    tableNumber: number;
    customerName: string;
    delivery: OrderDeliveryFormValues;
  }) => {
    try {
      const { delivery, ...restData } = data;
      // Update order info.
      setLoading(true);
      await api.updateOrderInformation(order.orderId, {
        ...restData,
        ...(delivery
          ? { delivery: transformFromDeliveryFormValues(delivery) }
          : {}),
      });

      message.success('Update success');
      onClose();
    } catch (e: any) {
      message.error('Failed to update: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Form
        form={form}
        name="orderInfo"
        initialValues={initialValues}
        layout="vertical"
        onFinish={handleFinish}
        size="large"
      >
        <Form.Item name="orderType" label="Order Type">
          <Select
            onChange={handleOrderTypeChange}
            options={ORDER_TYPE_OPTIONS}
            placeholder="Order Type"
          ></Select>
        </Form.Item>
        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) =>
            prevValues.orderType !== currentValues.orderType
          }
        >
          {({ getFieldValue }) => {
            const orderTypeVal = getFieldValue('orderType');
            const isRequireTableNumber =
              checkIsRequireTableNumber(orderTypeVal);
            const needDelivery = orderTypeVal === OrderType.Indent;

            return (
              <>
                <Form.Item
                  name="tableNumber"
                  label="Meja"
                  rules={
                    isRequireTableNumber
                      ? [
                          {
                            required: true,
                            message: 'Meja harus diisi',
                          },
                        ]
                      : []
                  }
                >
                  <Select
                    disabled={!isRequireTableNumber}
                    options={tableOptions}
                    placeholder="Table"
                  />
                </Form.Item>
                <Form.Item
                  label="Nama Customer"
                  name="customerName"
                  rules={
                    orderTypeVal === OrderType.TakeAway
                      ? [
                          {
                            required: true,
                            message: 'Nama harus diisi untuk takeaway',
                          },
                        ]
                      : []
                  }
                >
                  <Input />
                </Form.Item>
                {needDelivery ? (
                  <div>
                    <p className="text-2xl mb-4">Detail Indent</p>
                    <DeliveryFormFields prefix={['delivery']} />
                  </div>
                ) : null}
              </>
            );
          }}
        </Form.Item>
        <Form.Item>
          <Button
            className="w-full"
            type="primary"
            htmlType="submit"
            disabled={!isUserAllowToUpdate}
          >
            Update
          </Button>
        </Form.Item>
      </Form>
      <Typography.Text type="secondary">
        Notes: only admins can update
      </Typography.Text>
    </div>
  );
};

const useOpenOrderInfoPanel = (refreshOrderDetail: () => void) => {
  const { openDrawer } = useContext(DrawerContext);
  return useCallback((order: Order) => {
    openDrawer(
      ({ closeDrawer }) => {
        const handleClose = () => {
          refreshOrderDetail();
          closeDrawer();
        };
        return <OrderInfoPanel onClose={handleClose} order={order} />;
      },
      {
        title: 'Update Order Info',
        height: '80%',
        placement: 'bottom',
        closable: true,
      }
    );
  }, []);
};

export default useOpenOrderInfoPanel;
